// TelegramChatBinding — bind a Telegram group chat to an agency, a client, or
// the admin ops singleton (Wave 9.3).
//
// SAVE IS PHYSICALLY GATED on the bot actually being usable in the chat: the
// admin must run a DRY-RUN validate (POST /admin/telegram/validate-chat) on the
// CURRENT input value and get a pass before Save enables — any input change
// invalidates the prior validation. The backend re-validates live on every PUT
// anyway (defense in depth), and a PUT refusal is rendered exactly like a
// validate refusal.
//
// SECURITY: the browser never sees the bot token — validation and binding are
// entirely server-side; this component only renders what the admin backend
// returns. Unbind is confirm-gated like the other destructive admin actions.

import { useEffect, useId, useState } from "react";
import { CheckIcon } from "lucide-react";
import { toast } from "sonner";

import {
  bindChat,
  listOpsChats,
  setChatLanguage,
  unbindChat,
  type BindingKind,
  type ChatBinding,
  type ChatLanguage,
  type ChatRefusal,
  type ValidateChatResult,
  validateChat,
  LANGUAGE_DEFAULT,
} from "@/api/telegram";
import { errMessage } from "@/lib/errMessage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Modal, StatusPill } from "@/components/nexus";
import { NativeSelect } from "@/routes/nexus/controls";

// The three supported per-chat languages, in menu order.
const LANGUAGE_OPTIONS: { value: ChatLanguage; label: string }[] = [
  { value: "ru", label: "Русский" },
  { value: "en", label: "English" },
  { value: "zh", label: "中文" },
];

// Telegram chat ids: optionally-negative integers (groups/supergroups are
// negative, e.g. -1001234567890). ≤52 significant bits, so Number() is exact.
const CHAT_ID_RE = /^-?\d+$/;

const OWNER_NOUN: Record<BindingKind, string> = {
  client: "client",
  agency: "agency",
  admin: "admin ops",
};

// Human message for each structured refusal reason. The server `hint` is
// appended verbatim by the panel below.
function refusalMessage(refusal: ChatRefusal): string {
  switch (refusal.reason) {
    case "chat_not_found":
      return "Chat not found — is the ID right and is the bot added?";
    case "not_a_group":
      return "That chat is not a group — bind a group or supergroup chat.";
    case "privacy_mode_on":
      return "The bot's privacy mode is on, so it cannot read this group. Turn privacy mode off in @BotFather, then validate again.";
    case "bot_not_in_chat":
      return "The bot is not in that chat — add it to the group, then validate again.";
    case "telegram_unavailable":
      return "Telegram is unavailable right now — try again in a moment.";
    case "chat_already_bound":
      return `This chat is already bound to another ${refusal.bound_kind ?? "owner"}.`;
    case "ops_bot_not_configured":
      return "Ops bot is not configured yet — the Telegram bot token hasn't been loaded on the server.";
    default:
      return "The chat could not be validated.";
  }
}

// Render one validate/bind outcome: green pass, an informational setup-hint
// for ops_bot_not_configured (NOT an error wall), or a refusal alert with the
// server hint verbatim.
function ValidationPanel({ result }: { result: ValidateChatResult }) {
  if (result.ok) {
    return (
      <p className="flex items-start gap-1.5 rounded-md bg-nx-credit-soft px-3 py-2 text-xs font-medium text-nx-credit">
        <CheckIcon aria-hidden className="mt-0.5 size-3.5 shrink-0" />
        <span>
          {result.bot_status === "administrator"
            ? `Bot is admin in "${result.chat_title}"`
            : `Bot is member (privacy off) in "${result.chat_title}"`}
        </span>
      </p>
    );
  }
  if (result.reason === "ops_bot_not_configured") {
    return (
      <div className="rounded-md bg-nx-brand-soft px-3 py-2 text-xs text-nx-brand">
        <p className="font-medium">{refusalMessage(result)}</p>
        {result.hint && <p className="mt-1">{result.hint}</p>}
      </div>
    );
  }
  return (
    <div
      role="alert"
      className="rounded-md bg-nx-danger-soft px-3 py-2 text-xs text-nx-danger"
    >
      <p className="font-medium">{refusalMessage(result)}</p>
      {result.hint && <p className="mt-1">{result.hint}</p>}
    </div>
  );
}

export function TelegramChatBinding({
  kind,
  ownerId,
  currentBinding,
  onChanged,
}: {
  kind: BindingKind;
  // Required for kind 'agency' | 'client'; unused for 'admin'.
  ownerId?: string;
  // When provided (a binding or null for "known none"), the component skips
  // its own GET /admin/ops-chats lookup and stays in sync with the prop.
  currentBinding?: ChatBinding | null;
  onChanged?: (binding: ChatBinding | null) => void;
}) {
  const inputId = useId();
  const langId = useId();
  const selfLoad = currentBinding === undefined;

  const [binding, setBinding] = useState<ChatBinding | null>(
    currentBinding ?? null,
  );
  const [loading, setLoading] = useState(selfLoad);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Edit/bind form. `validated` records the exact input value a validation ran
  // against — Save enables only when it matches the current input AND passed.
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState("");
  const [validated, setValidated] = useState<{
    value: string;
    result: ValidateChatResult;
  } | null>(null);
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmUnbind, setConfirmUnbind] = useState(false);
  const [unbinding, setUnbinding] = useState(false);
  const [langBusy, setLangBusy] = useState(false);

  // Self-load the current binding from the global bindings list when the
  // parent did not supply one.
  useEffect(() => {
    if (!selfLoad) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    listOpsChats()
      .then((res) => {
        if (cancelled) return;
        const row =
          (res.chats ?? []).find(
            (c) =>
              c.active &&
              c.kind === kind &&
              (kind === "admin" ||
                (kind === "agency"
                  ? c.agency_id === ownerId
                  : c.client_id === ownerId)),
          ) ?? null;
        setBinding(row);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(errMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selfLoad, kind, ownerId]);

  // Stay in sync when the parent owns the binding.
  useEffect(() => {
    if (currentBinding !== undefined) setBinding(currentBinding);
  }, [currentBinding]);

  const trimmed = input.trim();
  const inputFormatOk =
    CHAT_ID_RE.test(trimmed) &&
    Number.isSafeInteger(Number(trimmed)) &&
    Number(trimmed) !== 0;
  const inputFormatError =
    trimmed !== "" && !inputFormatOk
      ? "A chat ID is a number — group chats are negative, e.g. -1001234567890."
      : null;

  const canSave =
    validated !== null &&
    validated.value === trimmed &&
    validated.result.ok &&
    !saving &&
    !validating;

  function resetForm() {
    setInput("");
    setValidated(null);
    setEditing(false);
  }

  async function onValidate() {
    if (!inputFormatOk || validating) return;
    setValidating(true);
    try {
      const result = await validateChat(Number(trimmed));
      setValidated({ value: trimmed, result });
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setValidating(false);
    }
  }

  async function onSave() {
    if (!canSave) return;
    setSaving(true);
    try {
      // Send the kind default explicitly so a fresh bind stores it.
      const result = await bindChat(
        kind,
        ownerId,
        Number(trimmed),
        LANGUAGE_DEFAULT[kind],
      );
      if (result.ok) {
        setBinding(result.binding);
        resetForm();
        toast.success("Telegram chat bound");
        onChanged?.(result.binding);
      } else {
        // The server re-validated and refused — render it exactly like a
        // validate failure (this also re-disables Save, since ok=false).
        setValidated({ value: trimmed, result });
      }
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setSaving(false);
    }
  }

  // Optimistically apply a language change, rolling back + toasting on failure.
  async function onLanguageChange(next: ChatLanguage) {
    if (!binding || next === (binding.language ?? LANGUAGE_DEFAULT[kind])) return;
    const prev = binding;
    const optimistic = { ...binding, language: next };
    setBinding(optimistic);
    setLangBusy(true);
    try {
      await setChatLanguage(kind, ownerId, next);
      onChanged?.(optimistic);
    } catch (err) {
      setBinding(prev);
      toast.error(errMessage(err));
    } finally {
      setLangBusy(false);
    }
  }

  async function onUnbind() {
    setUnbinding(true);
    try {
      await unbindChat(kind, ownerId);
      setBinding(null);
      setConfirmUnbind(false);
      toast.success("Telegram chat unbound");
      onChanged?.(null);
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setUnbinding(false);
    }
  }

  if (loading) {
    return <Skeleton className="h-16 w-full" />;
  }

  const showForm = binding === null || editing;

  return (
    <div className="grid gap-3">
      {loadError && (
        <p className="rounded-md bg-nx-danger-soft px-3 py-2 text-xs text-nx-danger">
          Could not load the current chat binding: {loadError}
        </p>
      )}

      {binding !== null ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-nx-border bg-nx-bg-2 px-3 py-2">
          <div className="grid gap-0.5">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-nx-text">
                {binding.title || String(binding.chat_id)}
              </span>
              <StatusPill
                tone={binding.bot_status === "administrator" ? "credit" : "na"}
              >
                {binding.bot_status ?? "unknown"}
              </StatusPill>
            </div>
            <span className="font-mono text-xs text-nx-text-3 tabular-nums">
              {binding.chat_id}
            </span>
            {binding.validated_at && (
              <span className="text-xs text-nx-text-3">
                Validated {new Date(binding.validated_at).toLocaleString()}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!editing && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setEditing(true)}
              >
                Change chat
              </Button>
            )}
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={unbinding}
              onClick={() => setConfirmUnbind(true)}
            >
              Unbind
            </Button>
          </div>
        </div>
      ) : null}

      {/* Per-chat notification language (only meaningful once bound). */}
      {binding !== null && (
        <div className="flex items-center gap-2">
          <label
            htmlFor={langId}
            className="text-xs font-medium tracking-wide text-nx-text-2 uppercase"
          >
            Language
          </label>
          <NativeSelect
            id={langId}
            className="max-w-40"
            value={binding.language ?? LANGUAGE_DEFAULT[kind]}
            disabled={langBusy}
            onChange={(e) => void onLanguageChange(e.target.value as ChatLanguage)}
          >
            {LANGUAGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </NativeSelect>
        </div>
      )}

      {binding === null && (
        <p className="text-sm text-nx-text-3">No Telegram chat bound.</p>
      )}

      {showForm && (
        <div className="grid gap-2">
          <label
            htmlFor={inputId}
            className="text-xs font-medium tracking-wide text-nx-text-2 uppercase"
          >
            Chat ID
          </label>
          <div className="flex items-start gap-2">
            <Input
              id={inputId}
              value={input}
              inputMode="numeric"
              spellCheck={false}
              autoComplete="off"
              placeholder="-1001234567890"
              className="max-w-60 font-mono"
              aria-invalid={inputFormatError != null}
              onChange={(e) => {
                setInput(e.target.value);
                // Any change invalidates the prior validation — Save re-locks
                // until the NEW value passes a validate.
                setValidated(null);
              }}
            />
            <Button
              type="button"
              variant="outline"
              disabled={!inputFormatOk || validating}
              onClick={onValidate}
            >
              {validating ? "Validating…" : "Validate"}
            </Button>
            <Button type="button" disabled={!canSave} onClick={onSave}>
              {saving ? "Saving…" : "Save"}
            </Button>
            {binding !== null && editing && (
              <Button type="button" variant="ghost" onClick={resetForm}>
                Cancel
              </Button>
            )}
          </div>
          <p className="text-xs text-nx-text-3">
            Add the bot to the {OWNER_NOUN[kind]} group, paste the group's chat
            ID, then validate. Save unlocks only after the bot is confirmed
            usable in that chat.
          </p>
          {inputFormatError && (
            <p role="alert" className="text-xs font-medium text-nx-danger">
              {inputFormatError}
            </p>
          )}
          {validated && validated.value === trimmed && (
            <ValidationPanel result={validated.result} />
          )}
        </div>
      )}

      {/* Unbind confirm (destructive, confirm-gated like the other admin writes). */}
      <Modal
        open={confirmUnbind}
        onOpenChange={(open) => {
          if (!open) setConfirmUnbind(false);
        }}
        title="Unbind this Telegram chat?"
        description={
          <>
            Notifications and ops messages for this {OWNER_NOUN[kind]} stop
            going to{" "}
            {binding?.title ? `"${binding.title}"` : "the bound chat"} (
            {binding?.chat_id}) immediately. You can bind it again later.
          </>
        }
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmUnbind(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={unbinding}
              onClick={onUnbind}
            >
              Confirm
            </Button>
          </>
        }
      />
    </div>
  );
}
